import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { BOOK_URL } from "@/lib/booking";

const TIERS = [
  {
    save: "£20",
    code: "CURATOR20",
    min: "£100",
    blurb: "Off any service",
  },
  {
    save: "£30",
    code: "CURATOR30",
    min: "£200",
    blurb: "Off bigger jobs",
  },
  {
    save: "£50",
    code: "CURATOR50",
    min: "£300",
    blurb: "Off premium jobs",
    featured: true,
  },
];

function CodeCard({ tier, i }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(tier.code).then(() => {
      setCopied(true);
      toast.success(`Code ${tier.code} copied — paste it in your quote request.`);
      setTimeout(() => setCopied(false), 2200);
    });
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
      className={`relative p-8 flex flex-col gap-6 border-2 transition-colors ${
        tier.featured
          ? "bg-[#FF5A36] text-[#F8F9FA] border-[#FF5A36] shadow-[8px_8px_0px_0px_rgba(248,249,250,0.15)]"
          : "bg-[#F8F9FA] text-[#0B1215] border-[#F8F9FA]/15 hover:border-[#FF5A36]"
      }`}
      data-testid={`launch-tier-${tier.code}`}
    >
      {tier.featured && (
        <div className="absolute top-4 right-4 bg-[#0B1215] text-[#F8F9FA] px-3 py-1 font-mono-label">
          · BIGGEST
        </div>
      )}

      <div>
        <div className={`font-mono-label ${tier.featured ? "text-[#0B1215]/80" : "text-[#495057]"}`}>
          · SAVE
        </div>
        <div className="font-display text-6xl sm:text-7xl tracking-tighter leading-none mt-1">
          {tier.save}
        </div>
        <div className={`font-mono-label mt-2 ${tier.featured ? "text-[#0B1215]/80" : "text-[#495057]"}`}>
          {tier.blurb} · over {tier.min}
        </div>
      </div>

      <button
        onClick={copy}
        className={`group flex items-center justify-between gap-3 px-5 py-4 border-2 font-mono w-full text-left transition-colors ${
          tier.featured
            ? "border-[#0B1215] hover:bg-[#0B1215] hover:text-[#F8F9FA]"
            : "border-[#0B1215] hover:bg-[#0B1215] hover:text-[#F8F9FA]"
        }`}
        data-testid={`copy-${tier.code}`}
      >
        <span className="font-display text-xl sm:text-2xl tracking-tight font-bold">{tier.code}</span>
        <span className="font-mono-label flex items-center gap-2">
          {copied ? (
            <>
              <Check size={14} /> COPIED
            </>
          ) : (
            <>
              <Copy size={14} /> COPY
            </>
          )}
        </span>
      </button>
    </motion.div>
  );
}

export default function LaunchOffer() {
  return (
    <section id="launch-offer" className="bg-[#0B1215] text-[#F8F9FA] py-20 sm:py-24 grain relative overflow-hidden">
      {/* Decorative orange bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF5A36]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start mb-12">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full bg-[#FF5A36] opacity-60" />
                <span className="relative inline-flex h-2.5 w-2.5 bg-[#FF5A36]" />
              </span>
              <span className="font-mono-label text-[#FF5A36]">· LAUNCH OFFER · LIMITED TIME</span>
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.92]">
              Up to <span className="text-[#FF5A36]">£50 off</span><br />
              your <span className="italic font-light">first</span> job.
            </h2>
            <p className="mt-5 text-[#F8F9FA]/70 leading-relaxed max-w-xl">
              A proper launch deal — pick the discount that fits the job, quote the code on your
              quote request, and I'll knock it straight off your final invoice. Stackable across any
              service: deep cleans, repairs, garden, carpets, the lot.
            </p>
          </div>

          <div className="lg:col-span-5 lg:pt-2">
            <div className="border-l-2 border-[#FF5A36] pl-5">
              <div className="font-mono-label text-[#F8F9FA]/50 mb-2">· HOW IT WORKS</div>
              <ol className="space-y-2 text-sm text-[#F8F9FA]/80">
                <li><span className="text-[#FF5A36] font-bold">01</span> · Pick the right code for the size of job</li>
                <li><span className="text-[#FF5A36] font-bold">02</span> · Paste it into your quote request</li>
                <li><span className="text-[#FF5A36] font-bold">03</span> · It's deducted from your final fixed quote</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="launch-tiers">
          {TIERS.map((tier, i) => (
            <CodeCard key={tier.code} tier={tier} i={i} />
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-8 border-t border-[#F8F9FA]/10">
          <p className="text-xs text-[#F8F9FA]/50 leading-relaxed max-w-2xl">
            <Sparkles size={12} className="inline -mt-0.5 mr-1 text-[#FF5A36]" />
            <strong className="text-[#F8F9FA]/80">Terms apply:</strong>{" "}
            One discount code per quote · cannot be combined · minimum spend applies to each tier
            (£100 / £200 / £300) · valid on services booked during the launch period · standard
            quotation conditions apply.
          </p>
          <a
            href={BOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#FF5A36] hover:bg-[#E04928] text-[#F8F9FA] px-6 py-4 font-mono-label active:scale-95 transition-all shrink-0"
            data-testid="launch-cta"
          >
            Claim my discount <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
